import { useState } from "react";
import { Trans } from "react-i18next";
import { Progress } from "@edusogno/primitives/progress.js";
import { INTERNAL_DATE_FORMAT } from "@/utils/agenda";
import { useAvailabilities, useIniPayments } from "@/data/user.ts";
import {
  Tabs,
  TabsList,
  TabsContent,
  TabsTrigger,
} from "@/components/ui/Tabs.tsx";
import Card from "./Card";
import dayjs from "dayjs";
import TabIcon from "./TabIcon";
import Spinner from "@/components/ui/Loader.tsx";
import MobileStats from "./MobileStats";
import Notifications from "./notifications/Index";
import CourseStructure from "./course-structure/MobileCourseStructure";
import useCourseDetails from "@/utils/hooks/useCourseDetails";
import AbsoluteBackground from "../ui/AbsoluteBackground";

const MobileView = () => {
  const { isLoading } = useAvailabilities();
  const { isLoading: paymentLoading } = useIniPayments();

  const {
    course,
    endDate,
    message,
    progress,
    startDate,
    lessonsDone,
    exercisesDone,
    recentlyDoneLessons,
    recentlyDoneExercises,
    percentageLessonsDone,
    percentageExecisesDone,
  } = useCourseDetails();

  const [currentTab, setCurrentTab] = useState<string>("stats");

  const noProgress = !(typeof progress === "number" && isFinite(progress));

  const kickoffDate = dayjs(startDate, INTERNAL_DATE_FORMAT).format(
    "D MMMM YYYY",
  );

  return isLoading || paymentLoading ? (
    <Spinner showText={false} className="!h-full" />
  ) : (
    <>
      <AbsoluteBackground className="bg-gradient z-[9]" />

      <div className="flex items-center px-4 flex-col z-[10] h-full w-full">
        <div className="flex gap-4 items-center overflow-y-auto flex-col w-full h-full max-w-md min-h-full">
          <Tabs
            value={currentTab}
            className="w-full flex flex-col h-full"
            defaultValue="stats"
            onValueChange={setCurrentTab}
          >
            <TabsList
              className={
                "w-full overflow-x-auto  flex-[0_0_2.5rem]  bg-transparent mb-2"
              }
            >
              <TabsTrigger
                value="stats"
                className="text-white px-2 text-xs py-1 data-[state=active]:text-primary  data-[state=active]:bg-[--primary300] rounded-md flex flex-row gap-1"
              >
                <TabIcon icon="stats" />
                <Trans
                  i18nKey="home.course.tab_three" // optional -> fallbacks to defaults if not provided
                  defaults="Your stats"
                />
              </TabsTrigger>

              <TabsTrigger
                value="course"
                className="text-white px-2 py-1 text-xs data-[state=active]:text-primary  data-[state=active]:bg-[--primary300] rounded-md flex flex-row gap-1 fat"
              >
                <TabIcon icon="course" />
                <Trans
                  i18nKey="home.course.tab_one" // optional -> fallbacks to defaults if not provided
                  defaults="Your course"
                />
              </TabsTrigger>

              <TabsTrigger
                value="notifications"
                className="text-white px-2 py-1 text-xs data-[state=active]:text-primary  data-[state=active]:bg-[--primary300] rounded-md flex flex-row gap-1 fat"
              >
                <TabIcon icon="notification" />
                <Trans
                  i18nKey="home.course.tab_two" // optional -> fallbacks to defaults if not provided
                  defaults="What's up next"
                />
              </TabsTrigger>
            </TabsList>

            <TabsContent value="stats" className="w-full flex-[1_1_100%]">
              <MobileStats
                {...{
                  message,
                  kickoffDate,
                  lessonsDone,
                  exercisesDone,
                  recentlyDoneLessons,
                  recentlyDoneExercises,
                  percentageLessonsDone,
                  percentageExecisesDone,
                }}
              />
            </TabsContent>

            <TabsContent value="course" className="w-full flex-[1_1_100%]">
              <CourseStructure course={course} />
            </TabsContent>

            <TabsContent
              value="notifications"
              className="w-full flex-[1_1_100%]"
            >
              <Notifications />
            </TabsContent>
          </Tabs>

          <Card
            key="boh"
            className="w-full h-fit flex flex-col gap-2 py-4 flex-[0_1_100px] px-6"
          >
            <div className="flex flex-row justify-between text-xs leading-[150%]">
              <Trans
                i18nKey="home.course.progress" // optional -> fallbacks to defaults if not provided
                defaults="Your course progress" // optional defaultValue
              />

              <p className="text-primary font-semibold">
                {!noProgress ? `${Math.ceil(progress ?? 0.0)}%` : "-"}
              </p>
            </div>

            <Progress size="sm" value={Math.ceil(progress ?? 0.0)} />

            <div className="flex flex-row items-center justify-between">
              {startDate !== undefined && dayjs(startDate).isValid() && (
                <div className="text-secondary bg-[--primary300] px-2 py-1 w-fit capitalize font-medium text-xs rounded-sm flex justify-center items-center">
                  {dayjs(startDate, INTERNAL_DATE_FORMAT).format("D MMM YYYY")}
                </div>
              )}

              {endDate !== undefined && dayjs(endDate).isValid() && (
                <div className="text-secondary bg-[--primary300] px-2 py-1 w-fit capitalize font-medium text-xs rounded-sm flex flex-row gap-3 justify-center items-center">
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 11 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.7302 0.920832C10.6502 0.885954 10.5615 0.873891 10.4745 0.886081C10.3876 0.898271 10.3061 0.934198 10.2399 0.989585C8.63594 2.32174 7.2772 1.67601 5.70249 0.927432C4.07108 0.15135 2.22142 -0.727036 0.1581 0.989585C0.1088 1.03062 0.0691932 1.08128 0.041928 1.1382C0.0146628 1.19511 0.000368142 1.25695 0 1.3196V10.56C0 10.6767 0.0482811 10.7886 0.134222 10.8711C0.220163 10.9536 0.336723 11 0.458262 11C0.5798 11 0.696361 10.9536 0.782302 10.8711C0.868242 10.7886 0.916523 10.6767 0.916523 10.56V8.12723C2.45113 6.96338 3.77321 7.59096 5.29579 8.31479C6.23522 8.76086 7.24684 9.24158 8.33177 9.24158C9.12972 9.24158 9.96662 8.98032 10.8419 8.25154C10.8912 8.2105 10.9308 8.15984 10.9581 8.10293C10.9853 8.04601 10.9996 7.98417 11 7.92152V1.3196C10.9995 1.2353 10.9739 1.1529 10.926 1.08221C10.8782 1.01151 10.8102 0.9555 10.7302 0.920832ZM0.916523 7.06404V4.50313C1.88174 3.8827 2.78623 3.86565 3.66609 4.09941V6.72522C2.79941 6.51566 1.88288 6.52886 0.916523 7.06404ZM10.0818 7.71361C9.1154 8.44624 8.23324 8.46825 7.33219 8.20368V5.58173C6.09374 5.25171 4.90455 4.42668 3.66609 4.09941V1.03744C4.19596 1.19309 4.73212 1.4472 5.29579 1.71507C5.94079 2.02143 6.61959 2.34374 7.33219 2.5159V5.58173C8.21148 5.81549 9.11597 5.79843 10.0818 5.17746V7.71361Z"
                      fill="#2D224C"
                    />
                  </svg>

                  {dayjs(endDate, INTERNAL_DATE_FORMAT).format("D MMM YYYY")}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default MobileView;
